import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Activity } from '@warema/shared/company-search/models';
import { AppointmentRequestActivityType, WorkflowOrigin, WorkflowStepType } from '@warema/shared/models';
import { TenantConfigurationService } from '@warema/shared/tenant-config';
import { WorkflowValidationApiService } from '@warema/shared/workflows/workflow-common/api';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { SearchDataService } from '../../search/services/search-data.service';
import * as ActivityActions from '../state/activity.actions';
import { ActivityState } from '../state/activity.reducers';
import * as ActivitySelectors from '../state/activity.selectors';

@Injectable({
    providedIn: 'root',
})
export class ActivityDataService {
    private readonly _store = inject<Store<ActivityState>>(Store);
    private readonly _route = inject(ActivatedRoute);
    private readonly _router = inject(Router);
    private readonly _searchDataService = inject(SearchDataService);
    private readonly _tenantConfig = inject(TenantConfigurationService);
    private readonly _workflowValidationApiService = inject(WorkflowValidationApiService);

    public activities$: Observable<Array<Activity>>;
    public selectedActivity$: Observable<AppointmentRequestActivityType>;

    constructor() {
        this.activities$ = this._store.select(ActivitySelectors.selectAllActivities);
        this.selectedActivity$ = this._store.select(ActivitySelectors.selectSelectedActivity);

        this._initActivityFromQueryParams();
    }

    public validateActivity(): void {
        const isFormValid$ = this.selectedActivity$.pipe(
            map(selectedActivity => !!selectedActivity && selectedActivity !== AppointmentRequestActivityType.Unknown),
        );

        this._workflowValidationApiService.manageWorkflowValidation(isFormValid$, WorkflowStepType.Activity);
    }

    public async handleActivityChange(
        currentActivity: AppointmentRequestActivityType,
        activityType: AppointmentRequestActivityType,
    ): Promise<void> {
        // default currentActivity is ‘ConsultationAtHome’, so this must be set the first time (if not already set)
        const activityTypeParam = this._route.snapshot.queryParams['activity'] as WorkflowStepType.Activity;

        if (!activityTypeParam || activityType !== currentActivity) {
            const originQueryParam = this._route.snapshot.queryParams['origin'] as WorkflowOrigin;
            if (!originQueryParam || (originQueryParam && originQueryParam !== WorkflowOrigin.BusinessCard)) {
                this._searchDataService.clearCompanySearchDataExceptLocationAndInstantOffer();
            }
            await this._router.navigate([], {
                relativeTo: this._route,
                queryParams: { activity: activityType.toString() },
                queryParamsHandling: 'merge',
            });
        }
    }

    public selectActivity(activityType: AppointmentRequestActivityType): void {
        this._store.dispatch(ActivityActions.ActivitySelected({ activityType }));
    }

    private _initActivityFromQueryParams(): void {
        this._route.queryParams
            .pipe(
                map(params => params['activity'] as string),
                distinctUntilChanged(),
            )
            .subscribe(activity => {
                if (!this._tenantConfig.settings?.companySearch.showActivities) {
                    activity = AppointmentRequestActivityType.ConsultationAtHome;
                }

                if (!activity) {
                    this.selectActivity(AppointmentRequestActivityType.Unknown);
                    return;
                }

                const castedActivity = activity as AppointmentRequestActivityType;
                if (Object.values(AppointmentRequestActivityType).includes(castedActivity)) {
                    this.selectActivity(castedActivity);
                } else {
                    this.selectActivity(AppointmentRequestActivityType.Unknown);
                }
            });
    }
}
